/** Grouping and ordering of issues for lists and boards. */
import type { Cycle, DisplayOptions, GroupKey, Id, Issue, Label, Project, ProjectMilestone, StateCategory, Team, User, WorkflowState } from "./types";
import { bySortKey, compareKeys } from "./order";
import { PRIORITIES, priorityRank } from "./format";

export interface GroupContext {
  states: Record<Id, WorkflowState>;
  users: Record<Id, User>;
  projects: Record<Id, Project>;
  cycles: Record<Id, Cycle>;
  labels: Record<Id, Label>;
  teams: Record<Id, Team>;
  milestones: Record<Id, ProjectMilestone>;
  /** Set when the view covers one team; status groups then use its exact statuses. */
  teamId?: Id;
}

export interface Group {
  key: string;
  label: string;
  kind: GroupKey;
  /** Raw value the group stands for (status id, user id, priority, category, ...). */
  value: string | number | null;
  color?: string;
  category?: StateCategory;
  issues: Issue[];
}

export const CATEGORY_ORDER: StateCategory[] = ["triage", "backlog", "unstarted", "started", "completed", "canceled"];
export const CATEGORY_LABEL: Record<StateCategory, string> = {
  triage: "Triage",
  backlog: "Backlog",
  unstarted: "Todo",
  started: "In Progress",
  completed: "Done",
  canceled: "Canceled",
};
export const CATEGORY_COLOR: Record<StateCategory, string> = {
  triage: "#F2994A",
  backlog: "#A1A7B3",
  unstarted: "#8B95A5",
  started: "#E8B634",
  completed: "#2F9E6E",
  canceled: "#9AA0A6",
};

export function sortIssues(issues: Issue[], orderBy: DisplayOptions["orderBy"] = "manual"): Issue[] {
  const list = [...issues];
  switch (orderBy) {
    case "priority":
      return list.sort((a, b) => priorityRank(a.priority) - priorityRank(b.priority) || bySortKey(a, b));
    case "updated":
      return list.sort((a, b) => compareKeys(b.updatedAt, a.updatedAt));
    case "created":
      return list.sort((a, b) => compareKeys(b.createdAt, a.createdAt));
    case "dueDate":
      return list.sort((a, b) => (a.dueDate ?? "9999") .localeCompare(b.dueDate ?? "9999") || bySortKey(a, b));
    case "title":
      return list.sort((a, b) => a.title.localeCompare(b.title));
    case "estimate":
      return list.sort((a, b) => (b.estimate ?? -1) - (a.estimate ?? -1) || bySortKey(a, b));
    default:
      return list.sort(bySortKey);
  }
}

/** Hides finished work older than the chosen window, and sub-issues when asked. */
export function visibleIssues(issues: Issue[], display: DisplayOptions, states: Record<Id, WorkflowState>): Issue[] {
  const completed = display.completed ?? "all";
  const cutoff = completed === "week" ? Date.now() - 7 * 86400_000 : completed === "month" ? Date.now() - 30 * 86400_000 : 0;
  return issues.filter((i) => {
    if (i.archivedAt) return false;
    if (display.showSubIssues === false && i.parentId) return false;
    const cat = states[i.stateId]?.category;
    if (cat === "completed" || cat === "canceled") {
      if (completed === "none") return false;
      const done = i.completedAt ?? i.canceledAt ?? i.updatedAt;
      if (cutoff && new Date(done).getTime() < cutoff) return false;
    }
    return true;
  });
}

export function groupIssues(issues: Issue[], by: GroupKey, ctx: GroupContext, opts: { showEmpty?: boolean; orderBy?: DisplayOptions["orderBy"] } = {}): Group[] {
  const groups = new Map<string, Group>();
  const add = (key: string, g: Omit<Group, "issues" | "key">) => {
    if (!groups.has(key)) groups.set(key, { key, ...g, issues: [] });
    return groups.get(key)!;
  };

  // Seed empty groups so columns exist before they have issues.
  if (by === "status") {
    if (ctx.teamId) {
      Object.values(ctx.states)
        .filter((s) => s.teamId === ctx.teamId && !s.archivedAt)
        .sort((a, b) => a.position - b.position)
        .forEach((s) => add(s.id, { label: s.name, kind: "status", value: s.id, color: s.color, category: s.category }));
    } else {
      CATEGORY_ORDER.forEach((c) => add(c, { label: CATEGORY_LABEL[c], kind: "status", value: c, color: CATEGORY_COLOR[c], category: c }));
    }
  } else if (by === "priority") {
    [1, 2, 3, 4, 0].forEach((p) => add(String(p), { label: PRIORITIES.find((x) => x.value === p)!.label, kind: "priority", value: p }));
  }

  for (const issue of issues) {
    switch (by) {
      case "none":
        add("all", { label: "All issues", kind: "none", value: null }).issues.push(issue);
        break;
      case "status": {
        const s = ctx.states[issue.stateId];
        if (ctx.teamId) add(issue.stateId, { label: s?.name ?? "Unknown", kind: "status", value: issue.stateId, color: s?.color, category: s?.category }).issues.push(issue);
        else {
          const c = s?.category ?? "backlog";
          add(c, { label: CATEGORY_LABEL[c], kind: "status", value: c, color: CATEGORY_COLOR[c], category: c }).issues.push(issue);
        }
        break;
      }
      case "assignee": {
        const u = issue.assigneeId ? ctx.users[issue.assigneeId] : undefined;
        add(issue.assigneeId ?? "none", { label: u?.name ?? "No assignee", kind: "assignee", value: issue.assigneeId }).issues.push(issue);
        break;
      }
      case "priority":
        add(String(issue.priority), { label: PRIORITIES.find((x) => x.value === issue.priority)!.label, kind: "priority", value: issue.priority }).issues.push(issue);
        break;
      case "project": {
        const p = issue.projectId ? ctx.projects[issue.projectId] : undefined;
        add(issue.projectId ?? "none", { label: p?.name ?? "No project", kind: "project", value: issue.projectId, color: p?.color }).issues.push(issue);
        break;
      }
      case "cycle": {
        const c = issue.cycleId ? ctx.cycles[issue.cycleId] : undefined;
        add(issue.cycleId ?? "none", { label: c ? c.name || `Cycle ${c.number}` : "No cycle", kind: "cycle", value: issue.cycleId }).issues.push(issue);
        break;
      }
      case "team": {
        const t = ctx.teams[issue.teamId];
        add(issue.teamId, { label: t?.name ?? "Team", kind: "team", value: issue.teamId, color: t?.color }).issues.push(issue);
        break;
      }
      case "milestone": {
        const m = issue.milestoneId ? ctx.milestones[issue.milestoneId] : undefined;
        add(issue.milestoneId ?? "none", { label: m?.name ?? "No milestone", kind: "milestone", value: issue.milestoneId }).issues.push(issue);
        break;
      }
      case "label": {
        const labels = issue.labelIds.map((id) => ctx.labels[id]).filter(Boolean);
        if (!labels.length) add("none", { label: "No label", kind: "label", value: null }).issues.push(issue);
        for (const l of labels) add(l.id, { label: l.name, kind: "label", value: l.id, color: l.color }).issues.push(issue);
        break;
      }
    }
  }

  let list = [...groups.values()];
  for (const g of list) g.issues = sortIssues(g.issues, opts.orderBy);
  if (!opts.showEmpty && by !== "status") list = list.filter((g) => g.issues.length > 0);
  if (!opts.showEmpty && by === "status") list = list.filter((g) => g.issues.length > 0 || (ctx.teamId && g.category !== "triage" && g.category !== "canceled"));

  // Stable, meaningful group order.
  const rank = (g: Group): [number, string] => {
    switch (by) {
      case "assignee":
        return [g.value == null ? 1 : 0, g.label.toLowerCase()];
      case "project":
      case "cycle":
      case "milestone":
      case "label":
        return [g.value == null ? 1 : 0, by === "cycle" && g.value ? ctx.cycles[g.value as string]?.startsOn ?? "" : g.label.toLowerCase()];
      case "team":
        return [0, g.label.toLowerCase()];
      default:
        return [0, ""];
    }
  };
  if (by !== "status" && by !== "priority" && by !== "none") {
    list.sort((a, b) => {
      const [ra, sa] = rank(a);
      const [rb, sb] = rank(b);
      return ra - rb || sa.localeCompare(sb);
    });
  }
  return list;
}

/** The fields to set on an issue when it's dropped into a group. */
export function patchForGroup(group: Group, issue: Issue, ctx: GroupContext, from?: Group): Record<string, unknown> | null {
  switch (group.kind) {
    case "status": {
      if (ctx.teamId || (typeof group.value === "string" && ctx.states[group.value])) return { stateId: group.value };
      const target = Object.values(ctx.states)
        .filter((s) => s.teamId === issue.teamId && s.category === group.value && !s.archivedAt)
        .sort((a, b) => a.position - b.position)[0];
      return target ? { stateId: target.id } : null;
    }
    case "assignee":
      return { assigneeId: group.value };
    case "priority":
      return { priority: group.value };
    case "project":
      return { projectId: group.value, milestoneId: null };
    case "cycle":
      if (group.value && ctx.cycles[group.value as string]?.teamId !== issue.teamId) return null;
      return { cycleId: group.value };
    case "milestone":
      return { milestoneId: group.value };
    case "team":
      return { teamId: group.value };
    case "label": {
      let labels = issue.labelIds.filter((l) => l !== from?.value);
      if (group.value && !labels.includes(group.value as string)) labels = [...labels, group.value as string];
      return { labelIds: labels.sort() };
    }
    default:
      return null;
  }
}
